<script setup lang="ts">
    import {computed, nextTick, ref, watch} from 'vue';
    import ChatApp from './ChatApp.vue';
    import {productCompare} from '../Store';
    import {formatLicensePlate} from './vehicle/licensePlate';
    import {vehiclePowerKw, vehicleTitle as formatVehicleTitle} from './vehicle/vehicleFormat';

    const props = defineProps<{
        conversationId: string;
        csrfToken: string;
        sendUrl: string;
        resetUrl: string;
        verifyReturnUrl: string;
        submitReturnUrl: string;
        verifyOrderStatusUrl: string;
        privacyUrl?: string;
        messageMaxLength: number;
        carBrand?: string;
        carModel?: string;
        carType?: string;
        carPlate?: string;
        carImage?: string;
        buttonLabel: string;
        // A page whose only way into the chat is the compare bar (a product page) leaves the button out.
        withoutButton?: boolean;
        modalTitle: string;
    }>();

    // Opening is what mounts the island and closing only hides it: a transcript the customer has
    // already built up survives being closed and reopened, while a page where the button is never
    // clicked never mounts the chat at all.
    const mounted = ref(false);
    const open = ref(false);

    /**
     * The car shown in the header. It starts as whatever the page said the shop already had
     * selected, so the panel can name the car the moment it opens, and is replaced by the real
     * thing as soon as the conversation resolves one.
     *
     * These props are for display only and are treated as such: they are never sent anywhere. The
     * vehicle the chat actually searches with is seeded server-side from the session
     * (ConversationStore::seedCarOnce), so editing them in the page source changes the label on
     * screen and nothing else.
     */
    const vehicle = ref<Record<string, any> | null>(
        [props.carBrand, props.carModel, props.carType, props.carPlate].some((value) => (value ?? '') !== '')
            ? {
                  carBrandName: props.carBrand ?? '',
                  modelName: props.carModel ?? '',
                  typeName: props.carType ?? '',
                  licensePlate: props.carPlate ?? '',
                  image: props.carImage ?? '',
              }
            : null
    );

    const plate = computed(() => formatLicensePlate(String(vehicle.value?.licensePlate ?? '')));
    const vehicleTitle = computed(() => (vehicle.value ? formatVehicleTitle(vehicle.value) : ''));
    const vehicleDetails = computed(() => {
        const kw = vehiclePowerKw(vehicle.value ?? {});
        const details = [vehicle.value?.engineType, kw !== null ? `${kw} kW` : null].filter(Boolean);

        return [...new Set(details.map(String))].join(' · ');
    });

    function show(): void {
        mounted.value = true;
        open.value = true;
    }

    // "Bekijk vergelijking" in the compare bar opens this chat and asks it for the comparison. Every
    // page with a compare bar mounts one of these, so the comparison always happens in place.
    const chatApp = ref<InstanceType<typeof ChatApp> | null>(null);
    const compare = productCompare();
    // The request is taken before anything is awaited: read after the await, a second run of this
    // watcher could find it already taken and send an empty comparison - the "Vergelijk deze 0
    // producten" and 422 seen on acceptance, though never reproduced. requestCompare() only queues
    // a request with at least two products, so a taken request is always one to send.
    watch(
        () => compare.pending,
        async () => {
            const request = compare.takePending();
            if (request === null) return;
            show();
            await nextTick();
            chatApp.value?.comparePageProducts(request);
        }
    );

    /**
     * The design gives the header both a minimise and a close control. They do the same thing
     * today, and deliberately so: closing already keeps the island mounted, so reopening lands
     * back in the same conversation either way. Only if "close" should end the conversation - drop
     * the transcript and the server-side state, the way "Nieuwe chat" does - do the two split.
     */
    function hide(): void {
        open.value = false;
    }
</script>

<template>
    <div class="chat-modal">
        <button
            v-if="!withoutButton"
            type="button"
            class="mao-button white"
            @click="show">
            {{ buttonLabel }}
        </button>

        <div
            v-if="mounted"
            v-show="open">
            <Modal
                no-header
                size="lg"
                @close="hide">
                <div class="panel">
                    <div class="panel__header">
                        <div class="panel__title">{{ modalTitle }}</div>

                        <div class="panel__tools">
                            <button
                                type="button"
                                class="panel__icon"
                                aria-label="Minimaliseren"
                                @click="hide">
                                <svg
                                    width="19"
                                    height="19"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    stroke-width="2.2"
                                    stroke-linecap="round"
                                    aria-hidden="true">
                                    <path d="M5 12h14" />
                                </svg>
                            </button>
                            <button
                                type="button"
                                class="panel__icon"
                                aria-label="Sluiten"
                                @click="hide">
                                <svg
                                    width="19"
                                    height="19"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    stroke-width="2.2"
                                    stroke-linecap="round"
                                    aria-hidden="true">
                                    <path d="M18 6 6 18M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                    </div>

                    <div
                        v-if="vehicle"
                        class="panel__vehicle"
                        :title="plate ? `Gekozen auto: ${vehicleTitle} (${plate})` : `Gekozen auto: ${vehicleTitle}`">
                        <div class="panel__vehicle-logo">
                            <img
                                v-if="vehicle.image"
                                :src="vehicle.image"
                                :alt="vehicle.carBrandName || ''" />
                            <svg
                                v-else
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="1.8"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                aria-hidden="true">
                                <path d="M5 17h14M6 17l-1-4 2-5h10l2 5-1 4M8 17v2M16 17v2" />
                                <circle
                                    cx="8"
                                    cy="14"
                                    r="1" />
                                <circle
                                    cx="16"
                                    cy="14"
                                    r="1" />
                            </svg>
                        </div>

                        <div class="panel__vehicle-copy">
                            <div class="panel__vehicle-title">{{ vehicleTitle || 'Gekozen auto' }}</div>
                            <div
                                v-if="vehicleDetails"
                                class="panel__vehicle-details text-muted">
                                {{ vehicleDetails }}
                            </div>
                        </div>

                        <span
                            v-if="plate"
                            class="panel__plate">
                            <span class="panel__plate-country">NL</span>
                            <span class="panel__plate-value">{{ plate }}</span>
                        </span>
                    </div>

                    <ChatApp
                        ref="chatApp"
                        class="panel__chat"
                        :conversation-id="conversationId"
                        :csrf-token="csrfToken"
                        :send-url="sendUrl"
                        :reset-url="resetUrl"
                        :verify-return-url="verifyReturnUrl"
                        :submit-return-url="submitReturnUrl"
                        :verify-order-status-url="verifyOrderStatusUrl"
                        :privacy-url="privacyUrl"
                        :message-max-length="messageMaxLength"
                        :initial-vehicle="vehicle"
                        @vehicle="(resolved) => (vehicle = resolved)" />
                </div>
            </Modal>
        </div>
    </div>
</template>

<style scoped lang="scss">
    @import '../../sass/globals';
    .chat-modal {
        margin: 12px 0 4px;
    }

    /* style.css sets width: 100% on every button; the trigger is a normal inline control. */
    .chat-modal > button {
        border-radius: 0;
        width: auto;
        height: 30px;
        font-size: 14px;
    }

    .chat-modal :deep(.modal) {
        z-index: 2147483553;
    }

    .chat-modal :deep(.modal-backdrop) {
        z-index: 2147483552;
    }

    /* The design's panel is one flush card: a 56px header and the chat filling the rest, with no
       modal padding of its own and no second scrollbar around the transcript. The site's modal
       supplies the backdrop, the centering and the click-outside-to-close; these override the
       spacing and height rules it ships with (including .modal .modal-body's own 70vh scroller
       in maoextra.css). Bootstrap also gives .modal-dialog a hard `width` at desktop widths, so a
       max-width on its own would never be reached - `width: 90%` (this selector already beats
       Bootstrap's own on specificity) keeps the dialog fluid down to the 700px breakpoint below,
       rather than staying rigid at the design's 660px cap until that breakpoint jumps it. */
    .chat-modal :deep(.modal-dialog) {
        width: 90%;
        max-width: 660px;
        margin: 30px auto;
    }

    .chat-modal :deep(.modal .modal-content) {
        display: flex;
        flex-direction: column;
        height: 90vh;
        padding: 0;
        overflow: hidden;
        border: 0;
        border-radius: 4px;
        box-shadow: 0 26px 70px rgba(0, 0, 0, 0.45);
    }

    .chat-modal :deep(.modal .modal-body) {
        display: flex;
        flex: 1 1 auto;
        flex-direction: column;
        min-height: 0;
        max-height: none;
        padding: 0;
        overflow: hidden;
    }

    .panel {
        border: none !important;
        display: flex;
        flex: 1 1 auto;
        flex-direction: column;
        min-height: 0;
        margin-bottom: 0;
    }

    .panel__header {
        display: flex;
        flex: 0 0 auto;
        align-items: center;
        justify-content: space-between;
        gap: 10px;
        height: 56px;
        padding: 0 14px 0 16px;
        border-bottom: 1px solid #e4e4e4;
    }

    .panel__title {
        overflow: hidden;
        font-size: 17px;
        font-weight: 700;
        line-height: 1;
        color: $black;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .panel__tools {
        display: flex;
        flex-shrink: 0;
        align-items: center;
        gap: 10px;
    }

    .panel__plate {
        display: flex;
        flex-shrink: 0;
        height: 26px;
        overflow: hidden;
    }

    .panel__vehicle {
        display: flex;
        flex: 0 0 auto;
        align-items: center;
        gap: 12px;
        min-width: 0;
        min-height: 62px;
        padding: 9px 16px;
        border-bottom: 1px solid #d7d7d7;
        background: $white;
    }

    /* A 42px logo tile in the header bar - the same number as --chat-indent by coincidence, not
       because it lines up with anything in the transcript. Sizing it off that token would couple
       the header to the avatar it has nothing to do with. */
    .panel__vehicle-logo {
        display: flex;
        flex: 0 0 42px;
        align-items: center;
        justify-content: center;
        width: 42px;
        height: 42px;
    }

    .panel__vehicle-logo img {
        width: 30px;
        height: 30px;
        object-fit: contain;
    }

    .panel__vehicle-copy {
        flex: 1 1 auto;
        min-width: 0;
    }

    .panel__vehicle-title {
        overflow: hidden;
        color: $black;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .panel__vehicle-details {
        margin-top: 2px;
        overflow: hidden;
        font-size: 12px;
        line-height: 1.25;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .panel__plate-country {
        display: flex;
        flex: 0 0 20px;
        align-items: center;
        justify-content: center;
        background: $link-blue;
        color: $white;
        font-size: 9px;
        font-weight: 700;
    }

    .panel__plate-value {
        display: flex;
        align-items: center;
        padding: 0 9px;
        background: $yellow;
        font-size: 13px;
        font-weight: 700;
        color: $black;
        letter-spacing: 0.04em;
    }

    .panel__icon {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 32px;
        height: 32px;
        padding: 0;
        border: 0;
        border-radius: 4px;
        background: none;
        color: $black;
        cursor: pointer;
    }

    .panel__icon:hover {
        background: rgba(50, 50, 50, 0.12);
    }

    .panel__chat {
        flex: 1 1 auto;
        min-height: 0;
    }

    @media (max-width: 700px) {
        .chat-modal :deep(.modal-dialog) {
            width: 100%;
            max-width: none;
            margin: 0;
        }

        .chat-modal :deep(.modal .modal-content) {
            height: 100vh;
            border-radius: 0;
        }

        .panel__vehicle {
            min-height: 58px;
            padding: 7px 12px;
        }

        .panel__vehicle-logo {
            flex-basis: 38px;
            width: 38px;
            height: 38px;
        }

        .panel__vehicle-title {
            font-size: 14px;
        }

        /* The trigger button keeps its full desktop sizing (40px height, 18px/700-weight text,
           25px horizontal padding) at every width today - on a phone that reads as oversized next
           to everything around it. */
        .chat-modal > button {
            padding: 0 16px;
        }
    }
</style>
